import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Col,
  Row,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  CardTitle,
  CardSubtitle,
  CardText,
  Input,
  Label,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import AdminPaymentPlanFeatures from './PlanFeatures';
import AdminPaymentPricingSelect from './PricingSelect';
import AdminPaymentSubmitButton from './SubmitButton';

import { openIntercomWidget } from '../../../helpers';
import { formatBytes } from '../../../helpers/Files';

import useForceUpdate from '../../../hooks/useForceUpdate';

const AdminPaymentPricing = ({
  client,
  paymentOptions,
  processing,
  handleCheckout,
  handleChangePlan,
}) => {
  const intl = useIntl();

  const currentPrice = paymentOptions.current || { id: null, interval: 'year', plan: null };

  const forceUpdate = useForceUpdate();
  const [interval, setInterval] = useState(currentPrice.interval);
  const [selectedPrices, setSelectedPrices] = useState({
    essential: null,
    pro: null,
    premium: null,
  });
  const [selectedPriceIndexes, setSelectedPriceIndexes] = useState({
    essential: 0,
    pro: 0,
    premium: 0,
  });

  const setInitialSelectedPrices = useCallback(() => {
    Object.keys(paymentOptions)
      .filter(po => (po !== 'current'))
      .forEach((po) => {
        const fprices = paymentOptions[po].prices
          .filter(p => (p.interval === interval));
        if (fprices.length > 0) {
          const selectedPrice = fprices[selectedPriceIndexes[po]];
          if (selectedPrice && selectedPrice.enabled) {
            selectedPrices[po] = selectedPrice.id;
          } else {
            selectedPrices[po] =
              fprices.filter(p => (p.enabled || p.id === currentPrice.id))[0]?.id;
          }

          if (currentPrice.id && currentPrice.interval === interval) {
            if (fprices.findIndex(p => (p.id === currentPrice.id)) >= 0) {
              selectedPrices[po] = currentPrice.id;
              const currentPriceIndex = fprices.findIndex(p => (p.id === currentPrice.id));
              if (currentPriceIndex >= 0) {
                selectedPriceIndexes[po] = currentPriceIndex;
                setSelectedPriceIndexes(selectedPriceIndexes);
              }
            }
          }

          if (!selectedPrice.enabled && selectedPrice.id !== currentPrice.id) {
            const idx = fprices.findIndex(p => (p.enabled || p.id === currentPrice.id));
            if (idx >= 0) {
              selectedPriceIndexes[po] = idx;
              setSelectedPriceIndexes(selectedPriceIndexes);
            }
          }
        }
      });
    forceUpdate();
  }, [
    interval,
    paymentOptions,
    selectedPrices,
    selectedPriceIndexes,
    currentPrice.id,
    currentPrice.interval,
    forceUpdate,
  ]);

  useEffect(() => {
    setInitialSelectedPrices();
  }, [setInitialSelectedPrices]);

  const handleChangeSelectedPrice = (paymentOption, priceId) => {
    selectedPrices[paymentOption] = priceId;
    setSelectedPrices(selectedPrices);
    selectedPriceIndexes[paymentOption] = paymentOptions[paymentOption].prices
      .filter(p => (p.interval === interval))
      .findIndex(p => (p.id === priceId)) || 0;
    setSelectedPriceIndexes(selectedPriceIndexes);
    forceUpdate();
  };

  const handleSwitchInterval = (e) => {
    setInterval(e.target.checked ? 'year' : 'month');
  };

  return (
    <>
      <Row>
        <Col className="pricing-switcher">
          <span className="pricing-switcher-title pricing-monthly">
            {intl.formatMessage({ defaultMessage: 'Monthly' })}
          </span>
          <Label for="switch-interval" className="switch">
            <div className="switch-control input-inner">
              <Input
                type="checkbox"
                name="interval"
                id="switch-interval"
                value="true"
                checked={interval === 'year'}
                onChange={handleSwitchInterval}
              />
              <i />
            </div>
          </Label>
          <span className="pricing-switcher-title pricing-annually">
            {intl.formatMessage({ defaultMessage: 'Annually' })}
          </span>
        </Col>
      </Row>
      <Row className="pricing-wrapper">
        <Col xs="12" sm="6" md="6" lg="6" xl="3" className={classnames('free-plan', { 'current-plan': currentPrice.plan === 'free' || !currentPrice.plan })}>
          {(currentPrice.plan === 'free' || !currentPrice.plan) && (
            <div className="current-plan-tag">
              {intl.formatMessage({ defaultMessage: 'Your current plan' })}
            </div>
          )}
          <Card className="price-card free-card">
            <CardHeader className="price-header">
              <CardTitle tag="h2">
                {intl.formatMessage({ defaultMessage: 'Free' })}
              </CardTitle>
              <CardSubtitle tag="p">
                {intl.formatMessage({
                  defaultMessage: 'The essentials you need for your team to start collaborating.',
                })}
              </CardSubtitle>
            </CardHeader>
            <CardBody className="price-body">
              <CardText tag="div">
                <ul className="fa-ul">
                  <li className="unlimited-users">
                    <span className="fa-li">
                      <i className="fa-regular fa-users" />
                    </span>
                    {intl.formatMessage({ defaultMessage: '10 Users' })}
                  </li>
                </ul>
                <div className="price-free-wrapper">
                  <span>
                    {currentPrice ? (
                      intl.formatMessage({
                        defaultMessage: '{storage} for free',
                      }, {
                        storage: paymentOptions.free.prices[0].storage_size,
                      })
                    ) : (
                      intl.formatMessage({
                        defaultMessage: '{storage} for free',
                      }, {
                        storage: formatBytes(client.file_size_limit),
                      })
                    )}
                  </span>
                </div>
              </CardText>
              <p className="center mt-4">
                {intl.formatMessage({
                  defaultMessage: 'Your current usage: <strong>{usedStorage}</strong>',
                }, {
                  usedStorage: formatBytes(client.used_storage) || 0,
                })}
              </p>
            </CardBody>
            <CardFooter className="price-footer">
              <h5>{intl.formatMessage({ defaultMessage: 'Free plan features:' })}</h5>
              <AdminPaymentPlanFeatures plan="free" />
            </CardFooter>
          </Card>
        </Col>
        <Col xs="12" sm="6" md="6" lg="6" xl="3" className={classnames('basic-plan', { 'current-plan': currentPrice.plan === 'essential' })}>
          {currentPrice.plan === 'essential' && (
            <div className="current-plan-tag">
              {intl.formatMessage({ defaultMessage: 'Your current plan' })}
            </div>
          )}
          <Card className="price-card basic-card">
            <CardHeader className="price-header">
              <CardTitle tag="h2">
                {paymentOptions.essential.name}
              </CardTitle>
              <CardSubtitle tag="p">
                {intl.formatMessage({ defaultMessage: 'Standard features - simple use of the platform.' })}
              </CardSubtitle>
            </CardHeader>
            <CardBody className="price-body">
              <CardText tag="div">
                <ul className="fa-ul">
                  <li className="unlimited-users">
                    <span className="fa-li">
                      <i className="fa-regular fa-users" />
                    </span>
                    {intl.formatMessage({ defaultMessage: 'Unlimited Users' })}
                  </li>
                </ul>
                <AdminPaymentPricingSelect
                  prices={paymentOptions.essential.prices}
                  interval={interval}
                  selectedPriceIndex={selectedPriceIndexes.essential}
                  currentPriceId={currentPrice.id}
                  handleChange={(e) => {
                    handleChangeSelectedPrice('essential', e.target.value);
                  }}
                />
                <AdminPaymentSubmitButton
                  selectedPriceId={selectedPrices.essential}
                  currentPriceId={currentPrice.id}
                  processing={processing}
                  handleCheckout={handleCheckout}
                  handleChangePlan={handleChangePlan}
                />
              </CardText>
            </CardBody>
            <CardFooter className="price-footer">
              <h5>{intl.formatMessage({ defaultMessage: 'Everything in Free, plus:' })}</h5>
              <AdminPaymentPlanFeatures plan="essential" />
            </CardFooter>
          </Card>
        </Col>
        <Col xs="12" sm="6" md="6" lg="6" xl="3" className={classnames('teams-plan', { 'current-plan': currentPrice.plan === 'pro' })}>
          {currentPrice.plan === 'pro' && (
            <div className="current-plan-tag">
              {intl.formatMessage({ defaultMessage: 'Your current plan' })}
            </div>
          )}
          <Card className="price-card teams-card">
            <CardHeader className="price-header">
              <CardTitle tag="h2">
                {paymentOptions.pro.name}
              </CardTitle>
              <CardSubtitle tag="p">
                {intl.formatMessage({ defaultMessage: 'Advanced features and flexible storage for growing teams.' })}
              </CardSubtitle>
            </CardHeader>
            <CardBody className="price-body">
              <CardText tag="div">
                <ul className="fa-ul">
                  <li className="unlimited-users">
                    <span className="fa-li">
                      <i className="fa-regular fa-users" />
                    </span>
                    {intl.formatMessage({ defaultMessage: 'Unlimited Users' })}
                  </li>
                </ul>
                <AdminPaymentPricingSelect
                  prices={paymentOptions.pro.prices}
                  interval={interval}
                  selectedPriceIndex={selectedPriceIndexes.pro}
                  currentPriceId={currentPrice.id}
                  handleChange={(e) => {
                    handleChangeSelectedPrice('pro', e.target.value);
                  }}
                />
                <AdminPaymentSubmitButton
                  selectedPriceId={selectedPrices.pro}
                  currentPriceId={currentPrice.id}
                  processing={processing}
                  handleCheckout={handleCheckout}
                  handleChangePlan={handleChangePlan}
                />
              </CardText>
            </CardBody>
            <CardFooter className="price-footer">
              <h5>{intl.formatMessage({ defaultMessage: 'Everything in Essential, plus:' })}</h5>
              <AdminPaymentPlanFeatures plan="pro" />
            </CardFooter>
          </Card>
        </Col>
        <Col xs="12" sm="6" md="6" lg="6" xl="3" className={classnames('premium-plan', { 'current-plan': currentPrice.plan === 'premium' })}>
          {currentPrice.plan === 'premium' && (
            <div className="current-plan-tag">
              {intl.formatMessage({ defaultMessage: 'Your current plan' })}
            </div>
          )}
          <Card className="price-card premium-card">
            <CardHeader className="price-header">
              <CardTitle tag="h2">
                {paymentOptions.premium.name}
              </CardTitle>
              <CardSubtitle tag="p">
                {intl.formatMessage({ defaultMessage: 'Custom storage, integrated security and dedicated support.' })}
              </CardSubtitle>
            </CardHeader>
            <CardBody className="price-body">
              <CardText tag="div">
                <ul className="fa-ul">
                  <li className="unlimited-users">
                    <span className="fa-li">
                      <i className="fa-regular fa-users" />
                    </span>
                    {intl.formatMessage({ defaultMessage: 'Unlimited Users' })}
                  </li>
                </ul>
                <AdminPaymentPricingSelect
                  prices={paymentOptions.premium.prices}
                  interval={interval}
                  selectedPriceIndex={selectedPriceIndexes.premium}
                  currentPriceId={currentPrice.id}
                  handleChange={(e) => {
                    handleChangeSelectedPrice('premium', e.target.value);
                  }}
                />
                <AdminPaymentSubmitButton
                  disabled={paymentOptions.premium.prices
                    .filter(p => (p.interval === interval)).length === 0}
                  selectedPriceId={selectedPrices.premium}
                  currentPriceId={currentPrice.id}
                  processing={processing}
                  handleCheckout={handleCheckout}
                  handleChangePlan={handleChangePlan}
                />
                <p className="center">
                  {intl.formatMessage({ defaultMessage: 'Need more storage?' })}{' '}
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      openIntercomWidget('messages');
                    }}
                  >
                    {intl.formatMessage({ defaultMessage: 'Contact us' })}
                  </a>.
                </p>
              </CardText>
            </CardBody>
            <CardFooter className="price-footer">
              <h5>{intl.formatMessage({ defaultMessage: 'Everything in Pro, plus:' })}</h5>
              <AdminPaymentPlanFeatures plan="premium" />
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

AdminPaymentPricing.propTypes = {
  client: PropTypes.object.isRequired,
  paymentOptions: PropTypes.object.isRequired,
  processing: PropTypes.bool.isRequired,
  handleCheckout: PropTypes.func.isRequired,
  handleChangePlan: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(AdminPaymentPricing);
